import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import { FadeUp, MaskedLines } from "../components/Reveal";

const responsibilities = [
  "Full document design and visual direction",
  "Development and maintenance of the document's visual system",
  "Layout and formatting across hundreds of pages",
  "Information hierarchy and navigation",
  "Management of tables, financial information, supporting content and appendices",
  "Template creation and document standardisation",
  "Coordination of incoming content from multiple contributors",
  "Version control and document governance",
  "Quality assurance and consistency checking",
  "Management of deadlines and production workflow",
  "Preparation of the final submission-ready document",
];

const skills = [
  "Information Design",
  "Corporate Document Design",
  "Bid & Proposal Design",
  "Project Management",
  "Document Governance",
  "Stakeholder Coordination",
  "Typography",
  "Complex Layout",
  "Quality Assurance",
  "Version Control",
  "Confidential Projects",
  "High-Stakes Delivery",
];

const meta = [
  ["Client", "Valiant Integrated Services"],
  ["Context", "Aid-mission funding proposal, Mali"],
  ["Role", "Lead Document Designer & Project Manager"],
  ["Scale", "Hundreds of pages, single controlled document"],
];

const Bar = ({ w = "100%", tone = "bg-white/25" }) => (
  <div className={`h-1.5 rounded-sm ${tone}`} style={{ width: w }} />
);

function MockShell({ label, children, tall = false }) {
  return (
    <div className="group border border-white/10 transition-colors duration-500 hover:border-umber/50" data-testid={`mock-${label}`}>
      <div className={`relative overflow-hidden bg-[#121418] p-5 ${tall ? "min-h-64" : "min-h-48"}`}>
        {children}
      </div>
      <div className="flex items-center justify-between border-t border-white/10 px-4 py-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EA3B0]">{label}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#616775]">Representative, redacted</span>
      </div>
    </div>
  );
}

function PageFrame({ children, folio = "000" }) {
  return (
    <div className="flex h-full flex-col border border-white/15 bg-[#0B0C0E] p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="h-1 w-10 bg-white/20" />
        <div className="h-1 w-6 bg-umber/60" />
      </div>
      <div className="flex-1 space-y-2">{children}</div>
      <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-2">
        <div className="h-1 w-8 bg-white/15" />
        <span className="font-mono text-[8px] text-[#616775]">{folio}</span>
      </div>
    </div>
  );
}

export default function ValiantCase() {
  useEffect(() => {
    document.title = "UN Financial Bid Proposal · Haus Ov Harii";
  }, []);

  return (
    <article className="px-6 pt-32 lg:px-12" data-testid="valiant-case-study">
      <FadeUp>
        <Link
          to="/corporate"
          data-testid="back-to-route-link"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9EA3B0] transition-colors hover:text-white"
        >
          <ArrowLeft size={14} /> Corporate
        </Link>
      </FadeUp>

      <header className="mt-12 max-w-4xl" data-testid="valiant-hero">
        <p className="eyebrow mb-6">Valiant Integrated Services</p>
        <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
          <MaskedLines lines={["UN Financial", "Bid Proposal"]} delay={0.1} />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 text-base font-light leading-relaxed text-[#9EA3B0] sm:text-lg">
            I was responsible for the complete design and management of a substantial UN-facing
            financial bid proposal, a document running to hundreds of pages and involving highly
            sensitive information, produced in support of an aid mission in conflict-affected Mali.
          </p>
        </FadeUp>
      </header>

      <FadeUp className="mt-14" delay={0.1}>
        <dl className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4" data-testid="case-meta">
          {meta.map(([k, v]) => (
            <div key={k} className="bg-[#0B0C0E] p-5">
              <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">{k}</dt>
              <dd className="mt-2 text-sm text-[#F9F9FB]">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>

      <FadeUp className="mt-14">
        <figure className="overflow-hidden border border-white/10" data-testid="valiant-hero-image">
          <img
            src="/images/valiant-un-bid-submission.jpeg"
            alt="Printed Valiant Integrated Services proposal volumes prepared for submission"
            className="max-h-[80vh] w-full object-cover"
          />
          <figcaption className="border-t border-white/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">
            The printed submission volumes, prepared for delivery
          </figcaption>
        </figure>
      </FadeUp>

      <section className="mt-24 grid gap-8 md:grid-cols-12" aria-label="Project overview" data-testid="valiant-overview">
        <FadeUp className="md:col-span-4">
          <p className="eyebrow">01 · Project Overview</p>
        </FadeUp>
        <FadeUp className="md:col-span-7" delay={0.1}>
          <h2 className="text-2xl font-light tracking-tight sm:text-3xl">
            Turning a very large body of complex information into one coherent, submission-ready document.
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
            The assignment was to transform a substantial volume of financial, operational and
            supporting material into a professional proposal ready for submission to the United
            Nations. The document's scale, hundreds of pages, made consistency a discipline in
            itself: every page, table and appendix had to belong unmistakably to the same system.
          </p>
          <p className="mt-5 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
            The role covered both sides of that problem: the visual system that made the document
            legible and credible, and the production process that kept it controlled as content
            arrived, changed and was finalised.
          </p>
        </FadeUp>
      </section>

      <section className="mt-24" aria-label="Responsibilities" data-testid="valiant-responsibilities">
        <FadeUp>
          <p className="eyebrow mb-8">02 · My Responsibilities</p>
        </FadeUp>
        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {responsibilities.map((r, i) => (
            <FadeUp key={r} delay={(i % 3) * 0.06}>
              <div
                className="group flex h-full flex-col justify-between gap-8 bg-[#0B0C0E] p-6 transition-colors duration-500 hover:bg-[#121418]"
                data-testid={`responsibility-card-${i + 1}`}
              >
                <span className="font-mono text-xs text-[#616775] transition-colors duration-500 group-hover:text-umber">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-sm leading-relaxed text-[#F9F9FB]">{r}</p>
              </div>
            </FadeUp>
          ))}
          <div className="hidden bg-[#0B0C0E] sm:block" aria-hidden="true" />
        </div>
      </section>

      <section className="mt-24 grid gap-8 border-t border-white/10 pt-16 md:grid-cols-12" aria-label="The challenge" data-testid="valiant-challenge">
        <FadeUp className="md:col-span-4">
          <p className="eyebrow">03 · The Challenge</p>
        </FadeUp>
        <FadeUp className="md:col-span-7" delay={0.1}>
          <h2 className="text-2xl font-light tracking-tight sm:text-3xl">
            Scale, complexity, confidentiality and deadline pressure, all at the same time.
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
            A document of this size fails quietly: a style that drifts on page 140, a table that
            breaks its grid, a section nobody can navigate to. The proposal had to remain visually
            consistent and easy to move through despite its length, while carrying dense financial
            and operational information supplied by different stakeholders in different states of
            readiness.
          </p>
          <p className="mt-5 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
            This was not a layout exercise. It was a document-management and governance challenge:
            holding hundreds of pages to one standard, under deadline, with material that could
            not leak, drift or contradict itself.
          </p>
        </FadeUp>
      </section>

      <section className="mt-24" aria-label="Design system" data-testid="valiant-design-system">
        <FadeUp>
          <p className="eyebrow mb-4">04 · Design System</p>
          <p className="mb-10 max-w-xl text-sm leading-relaxed text-[#9EA3B0]">
            A disciplined document system carried the project. The mock-ups below are abstracted
            representations of that system. No confidential content is shown.
          </p>
        </FadeUp>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <FadeUp>
            <MockShell label="Cover & dividers" tall>
              <PageFrame folio="COVER">
                <div className="flex h-8 w-8 items-center justify-center rounded-full border border-umber/60">
                  <div className="h-2 w-2 rounded-full bg-umber/60" />
                </div>
                <div className="pt-6">
                  <Bar w="45%" tone="bg-white/40" />
                  <div className="mt-2"><Bar w="70%" /></div>
                  <div className="mt-2"><Bar w="55%" /></div>
                </div>
                <div className="pt-8"><Bar w="30%" tone="bg-umber/50" /></div>
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp delay={0.05}>
            <MockShell label="Page hierarchy" tall>
              <PageFrame folio="0.0">
                <div className="group/h">
                  <div className="transition-transform duration-300 hover:translate-x-1"><Bar w="60%" tone="bg-white/45" /></div>
                  <div className="ml-4 mt-3 transition-transform duration-300 hover:translate-x-1"><Bar w="50%" tone="bg-white/30" /></div>
                  <div className="ml-4 mt-2 transition-transform duration-300 hover:translate-x-1"><Bar w="45%" tone="bg-white/30" /></div>
                  <div className="ml-8 mt-3 transition-transform duration-300 hover:translate-x-1"><Bar w="40%" tone="bg-white/18" /></div>
                  <div className="ml-8 mt-2 transition-transform duration-300 hover:translate-x-1"><Bar w="35%" tone="bg-white/18" /></div>
                  <div className="ml-8 mt-2 transition-transform duration-300 hover:translate-x-1"><Bar w="42%" tone="bg-white/18" /></div>
                </div>
                <div className="pt-4"><Bar w="25%" tone="bg-umber/50" /></div>
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp delay={0.1}>
            <MockShell label="Typography system" tall>
              <PageFrame folio="TYPE">
                <p className="font-grot text-4xl font-medium tracking-tighter text-white/80">Aa</p>
                <div className="space-y-2 pt-2">
                  <Bar w="65%" tone="bg-white/40" />
                  <Bar w="80%" tone="bg-white/25" />
                  <Bar w="75%" tone="bg-white/15" />
                </div>
                <div className="flex gap-2 pt-3">
                  {["400", "500", "600"].map((w) => (
                    <span key={w} className="border border-white/15 px-2 py-0.5 font-mono text-[8px] text-[#9EA3B0]">{w}</span>
                  ))}
                </div>
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp delay={0.15}>
            <MockShell label="Tables & financials" tall>
              <PageFrame folio="4.2">
                <div className="grid grid-cols-4 gap-px bg-white/15">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <div key={i} className={`flex h-5 items-center justify-center bg-[#0B0C0E] ${i < 4 ? "opacity-100" : "opacity-80"}`}>
                      <div className={`h-0.5 w-3/5 ${i < 4 ? "bg-umber/60" : "bg-white/20"}`} />
                    </div>
                  ))}
                </div>
                <div className="pt-3"><Bar w="50%" /></div>
                <div className="pt-2"><Bar w="70%" tone="bg-white/15" /></div>
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp>
            <MockShell label="Headers & folios">
              <PageFrame folio="142">
                <Bar w="90%" tone="bg-white/15" />
                <Bar w="85%" tone="bg-white/15" />
                <Bar w="88%" tone="bg-white/15" />
                <Bar w="60%" tone="bg-white/15" />
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp delay={0.05}>
            <MockShell label="Callout panels">
              <PageFrame folio="7.1">
                <div className="border-l-2 border-umber/70 bg-white/[0.04] p-3">
                  <Bar w="70%" tone="bg-white/35" />
                  <div className="mt-2"><Bar w="55%" tone="bg-white/20" /></div>
                </div>
                <div className="pt-3"><Bar w="80%" tone="bg-white/15" /></div>
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp delay={0.1}>
            <MockShell label="Appendix structure">
              <PageFrame folio="A–F">
                <div className="flex flex-wrap gap-1.5">
                  {["A", "B", "C", "D", "E", "F"].map((t) => (
                    <span key={t} className="flex h-7 w-7 items-center justify-center border border-white/15 font-mono text-[9px] text-[#9EA3B0] transition-colors duration-300 hover:border-umber/60 hover:text-umber">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="space-y-2 pt-4">
                  <Bar w="75%" tone="bg-white/20" />
                  <Bar w="65%" tone="bg-white/20" />
                  <Bar w="70%" tone="bg-white/20" />
                </div>
              </PageFrame>
            </MockShell>
          </FadeUp>
          <FadeUp delay={0.15}>
            <MockShell label="Page spreads">
              <div className="grid h-full grid-cols-2 gap-2">
                <PageFrame folio="136">
                  <Bar w="80%" tone="bg-white/15" />
                  <Bar w="70%" tone="bg-white/15" />
                </PageFrame>
                <PageFrame folio="137">
                  <Bar w="75%" tone="bg-white/15" />
                  <Bar w="85%" tone="bg-white/15" />
                </PageFrame>
              </div>
            </MockShell>
          </FadeUp>
        </div>
      </section>

      <section className="mt-24 border-y border-white/10 py-16" aria-label="Project management and governance" data-testid="valiant-governance">
        <div className="grid gap-10 md:grid-cols-12">
          <FadeUp className="md:col-span-5">
            <p className="eyebrow mb-6">05 · Project Management &amp; Governance</p>
            <h2 className="text-2xl font-light tracking-tight sm:text-3xl">
              Owning the process, not just the pages.
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
              Alongside designing the document, I managed the document-development process itself.
              On an engagement of this sensitivity and scale, that is the difference between a
              document that is merely designed and one that is controlled.
            </p>
          </FadeUp>
          <div className="md:col-span-7">
            <div className="border-t border-white/10">
              {[
                ["Consistency held at scale", "One visual standard maintained across hundreds of pages, from cover to final appendix."],
                ["Content coordination", "Incoming material from multiple contributors gathered, tracked and integrated without losing the thread."],
                ["Change & version control", "Every revision logged and governed, so the current version was always known, and always the only one in play."],
                ["Issue resolution", "Formatting and structural problems found and fixed at source, before they could replicate across the document."],
                ["Controlled final output", "The document that left the building was checked, consistent and submission-ready, with no loose ends at the deadline."],
              ].map(([t, d], i) => (
                <FadeUp key={t} delay={i * 0.05} className="grid gap-2 border-b border-white/10 py-6 sm:grid-cols-12">
                  <span className="font-mono text-xs text-umber sm:col-span-2">{String(i + 1).padStart(2, "0")}</span>
                  <div className="sm:col-span-10">
                    <h3 className="text-base font-medium tracking-tight text-[#F9F9FB]">{t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-[#9EA3B0]">{d}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FadeUp className="mt-16">
        <div className="flex gap-5 border border-white/10 bg-[#121418] p-6 sm:p-8" data-testid="valiant-confidentiality-note">
          <Lock size={18} className="mt-0.5 shrink-0 text-umber" aria-hidden="true" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EA3B0]">A note on confidentiality</p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#9EA3B0]">
              This project involved highly sensitive information. Confidential operational,
              financial and submission content cannot be displayed publicly. Every visual on this
              page is redacted, recreated, abstracted or representative of the design system,
              not a reproduction of sensitive material.
            </p>
          </div>
        </div>
      </FadeUp>

      <section className="mt-24 grid gap-8 md:grid-cols-12" aria-label="Outcome and value" data-testid="valiant-outcome">
        <FadeUp className="md:col-span-4">
          <p className="eyebrow">06 · Outcome / Value</p>
        </FadeUp>
        <FadeUp className="md:col-span-7" delay={0.1}>
          <h2 className="text-2xl font-light tracking-tight sm:text-3xl">
            A coherent, controlled, professional submission, from a body of information that could easily have been neither.
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
            The value of the contribution was twofold: a consistent visual framework that made a
            very large document legible and credible to its UN audience, and a managed production
            process that carried it, accurately, confidentially and on time, through to final
            delivery. The submission stood as a single, disciplined piece of corporate
            communication worthy of the mission it supported.
          </p>
        </FadeUp>
      </section>

      <section className="mt-24" aria-label="Skills demonstrated" data-testid="valiant-skills">
        <FadeUp>
          <p className="eyebrow mb-8">07 · Skills Demonstrated</p>
        </FadeUp>
        <div className="flex flex-wrap gap-2">
          {skills.map((s, i) => (
            <FadeUp key={s} delay={i * 0.03}>
              <span
                className="inline-block border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EA3B0] transition-colors duration-300 hover:border-umber hover:text-umber"
                data-testid={`skill-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {s}
              </span>
            </FadeUp>
          ))}
        </div>
      </section>

      <Link
        to="/corporate/cathay-pacific-sustainability"
        data-testid="next-project-link"
        className="group mt-24 block border-t border-white/10 py-16"
      >
        <p className="eyebrow mb-4">Next corporate project</p>
        <span className="flex items-center justify-between gap-4">
          <span className="text-3xl font-light tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-5xl">
            Sustainability in Flight
          </span>
          <ArrowRight className="shrink-0 text-[#616775] transition-all duration-500 group-hover:translate-x-2 group-hover:text-umber" size={32} />
        </span>
      </Link>
    </article>
  );
}
